<template>
    <form class="form-subscription" @submit.prevent="isUpdateAccount ? update() : create()">
        <section class="controls-container">
            <button class="_approve">
                {{ formLoading ? 'Loading...' : 'Save' }}
            </button>
        </section>

        <section class="form-container">
            <h3 class="form-header">
                Account Details
            </h3>

            <section class="form-section subscription">
                <span class="form-title">
                    <span class="required">*</span> Session Type
                </span>

                <CustomSelect
                    v-if="storeModule === 'registeredAccount' || storeModule === 'accountRequest'"
                    class="select-box"
                    v-model="accountForm.categories"
                    :items="sessionTypeList"
                    item-key="type"
                    item-text="content"
                    placeholder="Session Type"
                    is-searchable
                    is-multiple
                    :is-loading="sessionTypeLoading"
                 />
            </section>

            <section class="form-section subscription">
                <span class="form-title">
                    <span class="required">*</span> Prebuilt Session Type
                </span>

                <CustomSelect
                    v-if="storeModule === 'registeredAccount' || storeModule === 'accountRequest'"
                    class="select-box"
                    v-model="accountForm.prebuilt_default"
                    :items="accountForm.categories"
                    item-key="type"
                    item-text="content"
                    placeholder="Prebuilt Session Type"
                    is-searchable
                    :is-loading="sessionTypeLoading"
                 />
            </section>

            <section class="form-section">
                <InputField
                    class="input-box"
                    label="Annual Charge"
                    placeholder="Annual Charge"
                    step="0.01"
                    type="number"
                    v-model="accountForm.price"
                    :required="true"
                />
            </section>

            <section class="form-section assign-to" v-if="storeModule !== 'distributor'">
                <span class="form-title">
                    Assign To
                </span>

                <section class="assign-container">
                    <SelectField
                        class="select-box"
                        placeholder="Country"
                        displayProp="country"
                        valueProp="country"
                        idProp="country"
                        :disabled="countryPartnerLoading || !countryPartnerList.length"
                        :items="countryPartnerList"
                        v-model="accountForm.partnerCountry"
                        @input="selectPartnerCountry($event)"
                    />

                    <SelectField
                        class="select-box"
                        placeholder="Assign to"
                        displayProp="company_name"
                        valueProp="company_name"
                        idProp="id"
                        :deselect="true"
                        :disabled="partnerLoading || countryPartnerLoading || !partnerList.length"
                        :items="partnerList"
                        v-model="accountForm.partner"
                    />
                </section>
            </section>

            <section class="form-section subscription">
                <span class="form-title">
                    <span class="required">*</span> Subscription
                </span>
                
                <section class="datepicker-section">
                    <DatePicker
                        class="datepicker-box"
                        :options="{
                            type:'date',
                            placeholder:'YYYY/MM/DD',
                            notBefore: new Date(),
                            notAfter: accountForm.subscription_end,
                            editable: true
                        }"
                        v-model="accountForm.subscription_start"
                    />

                    <DatePicker
                        class="datepicker-box"
                        :options="{
                            type:'date',
                            placeholder:'YYYY/MM/DD',
                            notBefore: new Date(),
                            editable: true
                        }"
                        :disabled="!accountForm.subscription_start"
                        v-model="accountForm.subscription_end"
                    />
                </section>
            </section>

            <section class="form-section">
                <InputField
                    class="input-box"
                    label="User Allocation"
                    placeholder="User Allocation"
                    type="number"
                    :limit="20"
                    v-model="accountForm.user_allocation"
                    :required="true"
                />
            </section>
        </section>
    </form>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import InputField from '@/components/utils/InputField'
import SelectField from '@/components/utils/SelectField'
import CustomSelect from '@/components/utils/CustomSelect'
import DatePicker from '@/components/utils/DatePicker'
import moment from 'moment'

export default {
    props: {
        storeModule: {
            type: String,
            default: '',
            required: true
        }
    },

    computed: {
        ...mapFields('accountForm', [
            'accountForm',
            'isFormComplete',
            'formLoading',
            'isUpdateAccount',
        ]),

        ...mapFields('global', [
            'countryList',
            'countryLoading',

            'countryZoneList',
            'countryZoneLoading',

            'countryPartnerList',
            'countryPartnerLoading',

            'partnerList',
            'partnerLoading',

            'sessionTypeList',
            'sessionTypeLoading'
        ]),
    },

    async created () {
        // if (this.storeModule !== 'distributor') {
        //     await this.$store.dispatch('global/getSessionTypeList')
        // }
    },

    methods: {
        /* Form Creation */
        async create () {
            if (!await this.isFormValid()) return

            const form = await this.appendForm()

            this.$store.dispatch(`${this.storeModule}/createAccount`, form)
        },

        /* Form Updating */
        async update () {
            if (!await this.isFormValid()) return

            const form = await this.appendForm()
			let payload = form

			if (this.storeModule === 'accountRequest') {
				payload = { form, router: this.$router }
			}

            this.$store.dispatch(`${this.storeModule}/updateAccount`, payload)
        },

        /* Form Validation */
        async isFormValid () {
            if (this.formLoading) return false

            // Check Form Completion
            await this.$store.commit(`accountForm/isFormComplete`, this.storeModule === 'distributor')

            if (!this.isFormComplete) {
                await this.$store.commit('modal/toggleNotification', {
                    notificationName: 'notification-banner',
                    notificationType: 'warning',
                    notificationDesc: 'Please fill up all fields.',
                })

                return false
            }

            return true
        },

        /* Form Append */
        appendForm () {
            const form = new FormData()

            form.append('account_status', this.accountForm.account_status)
            form.append('company_name', this.accountForm.company_name)
            form.append('first_name', this.accountForm.first_name)
            form.append('last_name', this.accountForm.last_name)
            form.append('country', this.accountForm.country.name)
            form.append('zone_name', this.accountForm.zone_name.zone_name.split(' ')[0])
            form.append('zip_code', this.accountForm.zip_code)
            form.append('contact_no', this.accountForm.contact_no)
            form.append('email', this.accountForm.email)
            
            form.append('subscription_start', moment(this.accountForm.subscription_start).format('YYYY-MM-DD'))
            form.append('subscription_end', moment(this.accountForm.subscription_end).format('YYYY-MM-DD'))
            form.append('user_allocation', this.accountForm.user_allocation)
            form.append('price', this.accountForm.price)
            form.append('country_code', this.accountForm.country_code.int_dialing)

            if (this.accountForm.password) form.append('password', this.accountForm.password)
            if (this.accountForm.company_email) form.append('host_email', this.accountForm.company_email)
            if (this.accountForm.company_type.name) form.append('company_type', this.accountForm.company_type.name)

            if (this.storeModule === 'registeredAccount' || this.storeModule === 'accountRequest') {
                if (this.accountForm.categories.length > 0) form.append('session_type', this.accountForm.categories.map(i=>i.type).join())
                if (this.accountForm.prebuilt_default.length > 0) form.append('prebuilt_default', this.accountForm.prebuilt_default.map(i=>i.type).join())
            }

            if (this.storeModule !== 'distributor') {
				if (this.accountForm.phone_number) form.append('phone_number', this.accountForm.phone_number)
				form.append('bldg_apt_name', this.accountForm.bldg_apt_name)
				form.append('street', this.accountForm.street)
				form.append('city', this.accountForm.city)
                form.append('list_type', 'all')
            }

            // Partner is not required
            if (this.accountForm.partner && Object.keys(this.accountForm.partner).length) {
                form.append('partner_id', this.accountForm.partner.id)
            }

            if (!this.isUpdateAccount) {
                form.append('password', this.accountForm.password)
                form.append('password_confirmation', this.accountForm.password_confirmation)
            } else {
                form.append('id', this.accountForm.id)
                form.append('type', this.storeModule !== 'accountOverview' ? 'all' : 'view')
            }
            
            return form
        },

        /* Template Functions */
        selectPartnerCountry ({ country }) {
			this.accountForm.partner = {}
            this.getPartnerList(country)
        },

        getPartnerList (countryName) {
            this.$store.dispatch('global/getPartnerList', countryName)
        },
    },

    components: {
        InputField,
        SelectField,
        DatePicker,
        CustomSelect
    },
}
</script>

<style lang="scss" scoped>
.form-subscription {
    width: 500px;
    
    .controls-container {
        width: 100%;
        display: flex;
        justify-content: flex-end;
        margin-bottom: 20px;

        button {
            padding: 10px 15px;
        }
    }

    .form-container {
        background: #fff;
        box-shadow: 0px 0px 3px #2B3F5829;
        padding: 20px;
        border-radius: 3px;
        display: flex;
        flex-direction: column;
        width: 100%;

        .form-header {
            padding-bottom: 10px;
            border-bottom: 1px solid #E6EAF2;
            margin-bottom: 20px;
            margin-top: 0;
            color: #2B3F58;
            font-weight: 400;
        }

        .form-section {
            display: flex;
            align-items: center;

            .form-title {
                font-weight: 400;
                width: 150px;
                flex-shrink: 0;
                font-size: 15px;
                text-align: left;
                transition: 0.3s;
                color: #778395;
                font-size: 12px;

                span {
                    color: #BC3211;
                }
            }

            &:hover {
                .form-title {
                    color: #2DC4B6;
                }
            }

            &.host-name {
                flex-direction: column;

                .form-title {
                    text-align: left;
                    display: block;
                    width: 100%;
                    padding: 10px 0;
                }

                &:hover {
                    .form-title {
                        color: #2DC4B6;
                    }

                    /deep/ .input-wrapper {
                        input {
                            border: 1px solid #2DC4B6;
                        }

                        label {
                            color: #2DC4B6;
                        }
                    }
                }

                .input-section {
                    display: flex;
                    width: 100%;

                    /deep/ .input-wrapper {
                        flex-direction: column;
                        flex: 1 1 auto;
                        width: auto;

                        &:first-child {
                            margin-right: 15px;
                        }

                        label {
                            padding-top: 0;
                        }
                    }
                }
            }

            &.subscription {
                flex-direction: column;

                .form-title {
                    width: 100%;
                    margin-bottom: 10px;
                }

                &:hover {
                    .form-title {
                        color: #2DC4B6;
                    }

                    /deep/ .datepicker-box {
                        /deep/ .input-field {
                            border: 1px solid #2DC4B6;
                        }

                        label {
                            transition: 0.3s;
                            color: #2DC4B6;
                        }
                    }
                }

                .datepicker-section {
                    display: flex;
                    width: 100%;

                    /deep/ .datepicker-box {
                        padding: 0;
                        flex: 1 1 auto;
                        width: unset;

                        &:first-child {
                            margin-right: 10px;
                        }

                        label {
                            height: auto;
                            padding: 0 0 10px 0;
                            font-weight: 600;
                        }

                        .input-field {
                            color: #778395;
                        }
                    }
                }
            }

            &.phone {
                flex-direction: column;

                .form-title {
                    width: 100%;
                    margin-bottom: 10px;
                }

                .input-container {
                    display: flex;
                    justify-content: flex-start;
                    width: 100%;

                    .select-box {
                        width: 200px;
                        margin-right: 15px;
                        
                        /deep/ .selected-item {
                            padding: 9px 10px;
                            color: #778395;
                        }
                    }
        
                    .country-code {
                        border: 1px solid #C6C6C6;
                        border-radius: 4px;
                        font-size: 13px;
                        width: 170px;
                        margin-right: 15px;
                        height: 37px;
                        background: darken(#fff, 0.8%);
                        display: flex;
                        align-items: center;
                        padding-left: 10px;
                        user-select: none;
                    }
                }
            }

            &.assign-to {
                flex-direction: column;

                .form-title {
                    font-weight: 400;
                    width: 100%;
                    flex-shrink: 0;
                    text-align: left;
                    transition: 0.3s;
                    color: #778395;
                    font-size: 12px;
                    margin-bottom: 10px;
                }

                .assign-container {
                    display: flex;
                    justify-content: flex-start;
                    width: 100%;

                    .select-box {
                        &:not(:last-child) {
                            margin-right: 10px; 
                        }
                    }
                }
            }

            &:not(:last-child) {
                margin-bottom: 20px;
            }

            /deep/ .input-box, /deep/ .select-box {
                width: 100%;

                label {
                    font-weight: 400;
                    font-size: 12px;
                    flex-shrink: 0;
                    color: #778395;
                }

                .input-field {
                    flex: 1 1 auto;
                    color: #778395;
                }

                .toggle-password {
                    top: 7px;
                    right: 1px;
                    font-size: 13px;
                }
            }
        }
    }

}
</style>